import React, { CSSProperties } from 'react';
import classNames from 'classnames';
import { compact, includes, isEmpty, makeArray, map, split } from '@libs/shared';

interface HighLightTextProps {
    className?: string;
    style?: CSSProperties;
    keywordClassName?: string;
    keywordStyle?: CSSProperties;
    keyword?: string | string[];
    text?: string;
    abbreviated?: boolean;
    title?: boolean;
}

const ABBREVIATIONS_LENGTH = 12;

const HighLightText: React.FC<HighLightTextProps> = ({
    style,
    className,
    keywordStyle,
    keywordClassName,
    keyword,
    text,
    abbreviated = true,
    title
}) => {
    const renderContent = () => {
        const sourceKeywords = makeArray(keyword);
        const keywords = map(sourceKeywords, (keyword) =>
            keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        );

        if (
            !text ||
            !keyword ||
            isEmpty(keywords) ||
            !new RegExp(`(${keywords.join('|')})`, 'i').test(text)
        ) {
            return text;
        }

        const parts = compact(split(text, new RegExp(`(${keywords.join('|')})`, 'ig')));
        const lowerCaseKeywords = map(sourceKeywords, (keyword) => keyword.toLowerCase());
        return map(parts, (part, index) => {
            const isKeyword = includes(lowerCaseKeywords, part.toLowerCase());
            if (index === 0 && !isKeyword && abbreviated && part.length > ABBREVIATIONS_LENGTH) {
                return <span key={index}>...{part.slice(part.length - ABBREVIATIONS_LENGTH)}</span>;
            } else if (isKeyword) {
                return (
                    <mark
                        key={index}
                        className={classNames('keyword', keywordClassName)}
                        style={keywordStyle}
                    >
                        {part}
                    </mark>
                );
            } else {
                return <span key={index}>{part}</span>;
            }
        });
    };

    return (
        <span
            className={classNames('fx-high-light-text', className)}
            style={style}
            title={title ? text : undefined}
        >
            {renderContent()}
        </span>
    );
};

export default HighLightText;
